import { InviteUserComponent } from './../../user/invite-user/invite-user.component';
import { SideMenuService } from './side-menu.service';
import { User } from './user';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Environment, ENVIRONMENT_DI } from '../../environment/environment';
import { IdentityService } from '../../core/identity/identity.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CurrentContextService } from 'app/core/current-context.service';
import { Account } from 'app/back-office/account.model';
import { TierAccess } from 'app/shared/account/tier.model';
import { SubscriptionHandlerBaseComponent } from 'app/core/subscription-handler.base';
import { SettingsService } from '../../shared/services/admin-settings.service';
import { TopLevelSideMenuService } from '../top-level-side-menu/top-level-side-menu.service';
import {
  isSubNavItemExpanded,
  resolveSubNavEntries,
  SubNavEntry,
  SubNavGroup,
  SubNavItem,
  SubNavSeparator,
  TopLevelNavItem,
} from '../navigation.config';
import { EarlyAccessFeatures } from 'app/core/identity/user-identity';
import { SiteRoleService } from 'app/core/identity/site-role.service';

@Component({
  selector: 'side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SideMenuComponent extends SubscriptionHandlerBaseComponent implements OnInit {
  @Output() public onCurrentColorChanged = new EventEmitter<string>();
  public user: User;
  public isOpenInMobile: boolean;
  public consentUrl = '/consent';
  public isFullHeight = true;
  public currentAccount: Account;
  public isProduction: boolean;
  public isDevelopment: boolean;
  public tierAccess: TierAccess;
  private readonly adminToggle = signal(false);

  private readonly topLevelService = inject(TopLevelSideMenuService);
  private readonly adminSettings = inject(SettingsService);
  public readonly activeTopLevel = this.topLevelService.activeTopLevel;
  private readonly earlyAccessFeatures = signal<readonly EarlyAccessFeatures[]>([]);
  public readonly activeSubItems = computed<readonly SubNavEntry[]>(() =>
    resolveSubNavEntries(this.activeTopLevel(), {
      isProduction: this.isProduction,
      earlyAccessFeatures: this.earlyAccessFeatures(),
      adminToggle: this.adminToggle(),
    }),
  );
  public readonly hasSubItems = computed<boolean>(() => this.activeSubItems().length > 0);

  private readonly groupState = signal<Map<string, boolean>>(new Map());

  public isGroupExpanded = (group: SubNavGroup): boolean => {
    const explicit = this.groupState().get(group.translationKey);
    if (explicit !== undefined) return explicit;
    if (this.groupContainsActiveItem(group)) return true;
    return group.defaultExpanded ?? false;
  };

  public toggleGroup(group: SubNavGroup): void {
    const current = this.isGroupExpanded(group);
    this.groupState.update((map) => {
      const next = new Map(map);
      next.set(group.translationKey, !current);
      return next;
    });
  }

  public isSeparator(entry: SubNavEntry): entry is SubNavSeparator {
    return (entry as { type?: string }).type === 'separator';
  }

  public isGroup(entry: SubNavEntry): entry is SubNavGroup {
    return (entry as { type?: string }).type === 'group';
  }

  private groupContainsActiveItem(group: SubNavGroup): boolean {
    const url = this.topLevelService.currentUrl().split('?')[0];
    return group.items.some((item) => !!item.url && (url === item.url || url.startsWith(item.url + '/')));
  }

  private readonly isSiteAdmin = inject(SiteRoleService).isAccountAdmin;

  public visibleChildren(item: SubNavItem): readonly SubNavItem[] {
    return (item.children ?? []).filter((child) => !child.siteAdminOnly || this.isSiteAdmin());
  }

  public itemChildrenVisible(item: SubNavItem): boolean {
    if (!this.visibleChildren(item).length || !item.url) return false;
    return isSubNavItemExpanded(item, this.topLevelService.currentUrl().split('?')[0]);
  }

  public shouldShowUpgradeTag(entry: SubNavEntry): boolean {
    if (this.isSeparator(entry) || this.isGroup(entry)) return false;
    const key = entry.upgradeTagAccessKey;
    if (!key) return false;
    return this.tierAccess?.[key] === false;
  }

  public shouldShowUpgradeTagForTopLevel(topLevel: TopLevelNavItem): boolean {
    const key = topLevel.upgradeTagAccessKey;
    if (!key) return false;
    return this.tierAccess?.[key] === false;
  }

  private authenticated = false;

  public readonly routerLinkActiveOptions = {
    paths: 'exact' as const,
    queryParams: 'subset' as const,
    matrixParams: 'ignored' as const,
    fragment: 'ignored' as const,
  };

  constructor(
    private readonly sideMenuService: SideMenuService,
    private readonly identityService: IdentityService,
    private router: Router,
    public dialog: MatDialog,
    @Inject(ENVIRONMENT_DI) environment: Environment,
    private readonly currentContextService: CurrentContextService,
    readonly changeDetector: ChangeDetectorRef,
  ) {
    super();
    this.isProduction = environment.isProduction;
    this.isDevelopment = environment.isDevelopment;
  }

  public get isOpen(): boolean {
    return this.authenticated && this.hasSubItems();
  }

  async ngOnInit(): Promise<void> {
    this.initializeUser();
    this.changeDetector.detectChanges();
    super.addSubscription(
      this.identityService.onSignout.subscribe({
        next: () => {
          this.authenticated = false;
        },
      }),
    );

    super.addSubscription(
      this.sideMenuService.userChanged.subscribe((u: User) => {
        this.setUpUser(u);
      }),
    );

    super.addSubscription(
      this.currentContextService.currentContextBS.subscribe((currentContext) => {
        if (currentContext) {
          this.currentAccount = currentContext.account;
          this.tierAccess = currentContext.tierAccess;
          this.earlyAccessFeatures.set(currentContext.user?.earlyAccessFeatures ?? []);
        }
      }),
    );

    super.addSubscription(
      this.adminSettings.settings$.subscribe((settings) => {
        if (settings) {
          this.adminToggle.set(settings.adminToggle);
        }
      }),
    );
  }

  public async initializeUser() {
    const initialUser = await this.sideMenuService.getCurrentUserAsync();
    this.setUpUser(initialUser);
  }

  private setUpUser(user: User) {
    this.user = user;
    this.authenticated = this.identityService.hasCurrentIdentity;
  }

  public openCloseMenu(): void {
    this.isOpenInMobile = !this.isOpenInMobile;
  }

  public closeMenu(): void {
    this.isOpenInMobile = false;
  }

  public async inviteUser() {
    this.dialog.open(InviteUserComponent, {
      width: '600px',
      disableClose: false,
      data: {},
      autoFocus: false,
    });
  }

  public closeInMobile(): void {
    this.isOpenInMobile = false;
  }

  public onSubItemClick(): void {
    this.closeInMobile();
  }
}
